import axios from 'axios';
import type {
  Team,
  TeamMember,
  TeamSettings,
  CreateTeamInput,
  UpdateTeamInput,
  AddTeamMemberInput,
  UpdateTeamMemberInput,
  UpdateTeamSettingsInput,
} from '../types/team';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Get auth token from localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('token');
};

// Create axios instance with auth header
const api = axios.create({
  baseURL: `${API_URL}/api/teams`,
});

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface ApiResponse<T> {
  success?: boolean;
  teams?: Team[];
  team?: Team;
  members?: TeamMember[];
  member?: TeamMember;
  settings?: TeamSettings;
  message?: string;
  error?: string;
}

class TeamService {
  /**
   * Get all teams in user's organization
   */
  async getTeams(): Promise<Team[]> {
    const response = await api.get<ApiResponse<Team[]>>('/');

    if (response.data.teams) {
      return response.data.teams;
    }

    throw new Error(response.data.message || 'Failed to fetch teams');
  }

  /**
   * Get user's teams (teams they are a member of)
   */
  async getMyTeams(): Promise<Team[]> {
    const response = await api.get<ApiResponse<Team[]>>('/my-teams');

    if (response.data.teams) {
      return response.data.teams;
    }

    throw new Error(response.data.message || 'Failed to fetch your teams');
  }

  /**
   * Get a specific team by ID
   */
  async getTeamById(teamId: number): Promise<Team> {
    const response = await api.get<ApiResponse<Team>>(`/${teamId}`);

    if (response.data.team) {
      return response.data.team;
    }

    throw new Error(response.data.message || 'Failed to fetch team');
  }

  /**
   * Create a new team
   */
  async createTeam(data: CreateTeamInput): Promise<Team> {
    const response = await api.post<ApiResponse<Team>>('/', data);

    if (response.data.team) {
      return response.data.team;
    }

    throw new Error(response.data.message || 'Failed to create team');
  }

  /**
   * Update a team
   */
  async updateTeam(teamId: number, data: UpdateTeamInput): Promise<Team> {
    const response = await api.put<ApiResponse<Team>>(`/${teamId}`, data);

    if (response.data.team) {
      return response.data.team;
    }

    throw new Error(response.data.message || 'Failed to update team');
  }

  /**
   * Delete a team
   */
  async deleteTeam(teamId: number): Promise<void> {
    const response = await api.delete<ApiResponse<void>>(`/${teamId}`);

    if (response.status !== 200 && response.status !== 204) {
      throw new Error(response.data.message || 'Failed to delete team');
    }
  }

  /**
   * Get team members
   */
  async getTeamMembers(teamId: number): Promise<TeamMember[]> {
    const response = await api.get<ApiResponse<TeamMember[]>>(`/${teamId}/members`);

    if (response.data.members) {
      return response.data.members;
    }

    throw new Error(response.data.message || 'Failed to fetch team members');
  }

  /**
   * Add a member to the team
   */
  async addTeamMember(teamId: number, data: AddTeamMemberInput): Promise<TeamMember> {
    const response = await api.post<ApiResponse<TeamMember>>(`/${teamId}/members`, data);

    if (response.data.member) {
      return response.data.member;
    }

    throw new Error(response.data.message || 'Failed to add team member');
  }

  /**
   * Update a team member's role
   */
  async updateTeamMember(teamId: number, userId: number, data: UpdateTeamMemberInput): Promise<TeamMember> {
    const response = await api.put<ApiResponse<TeamMember>>(`/${teamId}/members/${userId}`, data);

    if (response.data.member) {
      return response.data.member;
    }

    throw new Error(response.data.message || 'Failed to update team member');
  }

  /**
   * Remove a member from the team
   */
  async removeTeamMember(teamId: number, userId: number): Promise<void> {
    const response = await api.delete<ApiResponse<void>>(`/${teamId}/members/${userId}`);

    if (response.status !== 200 && response.status !== 204) {
      throw new Error(response.data.message || 'Failed to remove team member');
    }
  }

  /**
   * Get team settings
   */
  async getTeamSettings(teamId: number): Promise<TeamSettings> {
    const response = await api.get<ApiResponse<TeamSettings>>(`/${teamId}/settings`);

    if (response.data.settings) {
      return response.data.settings;
    }

    throw new Error(response.data.message || 'Failed to fetch team settings');
  }

  /**
   * Update team settings
   */
  async updateTeamSettings(teamId: number, data: UpdateTeamSettingsInput): Promise<TeamSettings> {
    const response = await api.put<ApiResponse<TeamSettings>>(`/${teamId}/settings`, data);

    if (response.data.settings) {
      return response.data.settings;
    }

    throw new Error(response.data.message || 'Failed to update team settings');
  }
}

const teamServiceInstance = new TeamService();
export default teamServiceInstance;
